import * as React from "react";
import { Button, Card, Divider, Space, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CardContent } from "../../components/antd/card/CardContent";
import { CardCollapsibleContent } from "../../components/antd/card/CardCollapsibleContent";
import { useDrawerContext } from "../../components/antd/drawerContext";
import { v4 as uuidv4 } from "uuid";
import { SecretTypeTag } from "../../components/antd/kindselect/tagRenderers/secretTypeTag";
import { formSetValue, useCpFormValue } from "./testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { CpSwitch } from "./testNewForm/CpSwitch";
import { CpLabel } from "./testNewForm/CpLabel";
import { CpFormItem } from "./testNewForm/CpFormItem";
import { CpErrorWarning } from "./testNewForm/CpErrorWarning";
import { CpSelect } from "./testNewForm/CpSelect";
import { CpInput } from "./testNewForm/CpInput";
import { CpArrayObject } from "./testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../workload/draft/CpArrayItemNameValuePair";
import { CpErrorWarningArrayIcon } from "./testNewForm/CpErrorWarningArrayIcon";
import { parseLink } from "../../utils/linkParser/linkParser";
import { CpKindSelect } from "./testNewForm/temp/CpKindSelect";
import { CpLabelTooltip } from "./testNewForm/CpLabelTooltip";
import { CpTooltip } from "./testNewForm/CpTooltip";
import { EmbeddedSecretCreate } from "../secret/create/embedded";

interface Props {}
export const Tracing: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const drawer = useDrawerContext();

  const isEnabled = useCpFormValue(`tracing.isEnabled`);
  const tracing = useCpFormValue(`tracing`);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            <div className={`flex items-center`}>
              <div>Tracing</div>
              <div style={{ transform: "translateY(0px)" }}>
                <CpErrorWarningArrayIcon names={["tracing"]} />
              </div>
            </div>
          </CardTitle>
          <CardContent>
            <CpFormItem>
              <div className="flex items-center gap-2">
                <CpLabel name={`tracing.isEnabled`}>Enable Tracing</CpLabel>
                <CpSwitch name={`tracing.isEnabled`} />
              </div>
              <CpErrorWarning name={`tracing.isEnabled`} />
            </CpFormItem>
            <CardCollapsibleContent collapsed={!isEnabled}>
              <CpFormItem>
                <CpLabel name={`tracing.provider`}>Provider</CpLabel>
                <CpSelect
                  style={{ maxWidth: 600 }}
                  name={`tracing.provider`}
                  options={[
                    { label: "Control Plane", value: "controlplane" },
                    { label: "Lightstep", value: "lightstep" },
                    { label: "Open Telemetry", value: "otel" },
                  ]}
                />
                <CpErrorWarning name={`tracing.provider`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`tracing.sampling`}>Sampling Percentage</CpLabel>
                <CpInput name={`tracing.sampling`} style={{ maxWidth: 600 }} />
                <CpErrorWarning name={`tracing.sampling`} />
              </CpFormItem>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  label="Custom Tags"
                  labels={["Key", "Value"]}
                  factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                  name={`tracing.customTags`}
                >
                  <CpArrayItemNameValuePair />
                </CpArrayObject>
              </div>
              {tracing.provider === "lightstep" ? (
                <>
                  <Divider orientation={"left"} orientationMargin={0}>
                    Lightstep
                  </Divider>
                  <CpFormItem>
                    <CpLabelTooltip>
                      <CpLabel name={`tracing.lightstep.credentialsSecret`}>Credentials</CpLabel>
                      <CpTooltip tooltipProps={{ title: "Must be an opaque secret" }} />
                    </CpLabelTooltip>
                    <Space.Compact style={{ maxWidth: 600 }}>
                      <CpKindSelect
                        kind="secret"
                        name={`tracing.lightstep.credentialsSecret`}
                        optionValue="name"
                        queries={[{ property: "type", value: "opaque" }]}
                        tagRenderers={[SecretTypeTag as any]}
                        style={{ minWidth: 600 }}
                        placeholder={"Select or type a Secret Item"}
                      />
                      <Tooltip title={`Create an Opaque Secret`}>
                        <Button
                          onClick={() => {
                            drawer.setPersistent(
                              `gvc-tracing-create-secret`,
                              "Create Ligtstep Credentials Opaque Secret",
                              <>
                                <EmbeddedSecretCreate
                                  onCancel={() => drawer.closePersistent()}
                                  onDone={(itemLink) => {
                                    drawer.closePersistent();
                                    const { name } = parseLink(itemLink, { useInputCtx: true });
                                    const secretState = {
                                      value: name,
                                      name: name,
                                      link: itemLink,
                                    };
                                    formSetValue(id, `tracing.lightstep.credentialsSecret`, secretState);
                                  }}
                                  lockedType={`opaque`}
                                />
                              </>,
                              600,
                            );
                          }}
                          icon={<PlusOutlined />}
                        />
                      </Tooltip>
                    </Space.Compact>
                    <CpErrorWarning name={`tracing.lightstep.credentialsSecret`} />
                  </CpFormItem>
                  <CpFormItem>
                    <CpLabelTooltip>
                      <CpLabel name={`tracing.lightstep.endpoint`}>Endpoint</CpLabel>
                      <CpTooltip
                        tooltipProps={{ title: `Values can be; test.com, <workload>.<gvc>.cpln.local[:<port>]` }}
                      />
                    </CpLabelTooltip>
                    <CpInput name={`tracing.lightstep.endpoint`} style={{ maxWidth: 600 }} />
                    <CpErrorWarning name={`tracing.lightstep.endpoint`} />
                  </CpFormItem>
                </>
              ) : null}
              {tracing.provider === "otel" ? (
                <>
                  <Divider orientation={"left"} orientationMargin={0}>
                    Open Telemetry
                  </Divider>
                  <CpFormItem>
                    <CpLabelTooltip>
                      <CpLabel name={`tracing.otel.endpoint`}>Endpoint</CpLabel>
                      <CpTooltip
                        tooltipProps={{ title: `Values can be; test.com, <workload>.<gvc>.cpln.local[:<port>]` }}
                      />
                    </CpLabelTooltip>
                    <CpInput name={`tracing.otel.endpoint`} style={{ maxWidth: 600 }} />
                    <CpErrorWarning name={`tracing.otel.endpoint`} />
                  </CpFormItem>
                </>
              ) : null}
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
